// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import { Component, OnInit, Input,  OnChanges, SimpleChanges } from '@angular/core';
import { D3eService } from 'src/services/d3e-service';
import { IDeviceSetBare } from 'src/types/device-sets';
import { URL_PARAMS } from 'src/types/device';
import { TranslateService } from '@ngx-translate/core';
import { SelectedOrgMethod } from 'src/shared/selectedOrg';
import { Theme, ThemeService } from '../../../../services/theme.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-device-config',
    templateUrl: './device.config.component.html',
    styleUrls: ['./device.config.component.scss', '../component.common.scss'],
    standalone: false
})

// @Directive({
//   selector: 'ngx-charts-guage[hotfix-zero-margin]'
// })

export class DeviceConfigComponent implements OnInit, OnChanges {
  @Input() deviceSet: IDeviceSetBare;
  @Input() refreshTrigger: number;
  @Input() selectedOrgId: string;

  loadingConfig$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  valueStyle = {
    display: 'flex',
    height: '20px',
    width: '25px',
    margin: '0',
    padding: '0'
  };
  initialLoad = true;
  isDarkTheme: boolean;
  theme: Observable<Theme>;
  totalDevices = 0;
  legend = false;
  legendPosition = 'bottom';
  showText = false;
  margin: any[] = [40, 0, 0, 60]; // top, bottom, right, left
  showAxis = false;
  colorScheme = {
    domain: ['#58a252', '#e3721d', '#0067B8', '#FA0F00', '#808000', '#0074c7']
  };
  bigSegments = 1;

  // The testTotal and testDisplay are used to automatically render
  // numbers for the gauge.  If testDisplay is true, then the testTotal
  // will be used at the number of total devices, and that value will
  // be increased every two seconds.  This can be used to see what the
  // gauge will look like with different numbers.
  testTotal = 15;
  testDisplay = false;

  fProtections: string;
  fProtectionsInfo: string;
  fProtectionsNote: string;
  fFolder: string;
  fCustomFile: string;

  view: any[] = [150, 190];
  data;

  dashboardConfigLoaded = false;

constructor(
            private d3eService: D3eService,
            private translate: TranslateService,
            private themeService: ThemeService,
  ) {
    this.translate.get(['DASH_PROTECTIONS', 'DASH_PROTECTIONS_NOTE', 'DASH_FOLDER', 'DASH_PROTECTIONS_INFO',
      'DASH_CUSTOM']).subscribe(vals => {
      this.fProtections = vals['DASH_PROTECTIONS'];
      this.fProtectionsInfo = vals['DASH_PROTECTIONS_INFO'];
      this.fProtectionsNote = vals['DASH_PROTECTIONS_NOTE'];
      this.fFolder = vals['DASH_FOLDER'];
      this.fCustomFile = vals['DASH_CUSTOM'];

      this.themeService.getTheme()
        .pipe(untilDestroyed(this))
        .subscribe(t => this.isDarkTheme = t === 'dark');

    });
    this.resetData(true);
    // gauge.margin = [0, 0, 0, 0];
  }

  ngOnInit() {
//    this.getDeviceConfig();
    if ( this.refreshTrigger == null ) {
      this.fProtectionsNote = '';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ( changes.deviceSet !== undefined ) {
      this.deviceSet = changes.deviceSet.currentValue;
    } 

    if ( changes.selectedOrgId !== undefined ) {
      if (!changes.selectedOrgId.firstChange) {
        this.initialLoad = false;
      }
      this.selectedOrgId = changes.selectedOrgId.currentValue;
      if (this.refreshTrigger == null) {
        this.resetData();
        this.getDeviceConfig();
      }
    }

    if ( changes.refreshTrigger !== undefined && changes.refreshTrigger.firstChange === false) {
      this.refreshTrigger = changes.refreshTrigger.currentValue;
    }

    if ( this.refreshTrigger ) {
      // This is hacky, but it's so the animation doesn't change too many times on initial page load.
      if (changes?.refreshTrigger?.previousValue > 0) {
        this.resetData();
      }
     this.getDeviceConfig();
    }
  }

  getColors(data) {
    const defaultColor = this.isDarkTheme ? 'rgba(255,255,255,0.67)' : 'rgba(0,0,0,0.67)';
    return data['value'] > 0 ? data['color'] : defaultColor;
  }
  
  public getDeviceConfig() {
    this.loadingConfig$.next(true);
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
    const queryJson = { 'selectedOrgId': selectedOrg };

    let deviceId = null;
    queryJson['where'] = { 'deactivated': 0 };
    
    if ( this.deviceSet !== undefined && this.deviceSet != null) {
      deviceId = this.deviceSet.id;
      queryJson['sets'] = [deviceId];
    }

    this.d3eService.get_home_device_config_dashboard(queryJson)
      .pipe(untilDestroyed(this))
      .subscribe(results => {
        const urlParams = {};
        if ( deviceId != null) {
          urlParams['deviceSetId'] = deviceId.toString();
        }

        const folderParams = JSON.parse(JSON.stringify(urlParams));
        folderParams[URL_PARAMS.PROTECTION_PARAM_NAME] = URL_PARAMS.PROTECTION_FOLDER;
        const customFileParams = JSON.parse(JSON.stringify(urlParams));
        customFileParams[URL_PARAMS.PROTECTION_PARAM_NAME] = URL_PARAMS.PROTECTION_CUSTOM_FILE;
        const officeParams = JSON.parse(JSON.stringify(urlParams));
        officeParams[URL_PARAMS.PROTECTION_PARAM_NAME] = URL_PARAMS.PROTECTION_OFFICE;
        const adobeParams = JSON.parse(JSON.stringify(urlParams));
        adobeParams[URL_PARAMS.PROTECTION_PARAM_NAME] = URL_PARAMS.PROTECTION_ADOBE;
        const secureDriveParams = JSON.parse(JSON.stringify(urlParams));
        secureDriveParams[URL_PARAMS.PROTECTION_PARAM_NAME] = URL_PARAMS.PROTECTION_SECURE_DRIVE;

        this.data = [
          {'name': this.fFolder, 'value': results['folder'], 'color': this.colorScheme.domain[0], 'urlParams': folderParams},
          // tslint:disable-next-line:max-line-length
          {'name': this.fCustomFile, 'value': results['customFileType'], 'color': this.colorScheme.domain[1], 'urlParams': customFileParams},
          {'name': 'Microsoft Office', 'value': results['msOffice'], 'color': this.colorScheme.domain[2], 'urlParams': officeParams},
          {'name': 'Adobe', 'value': results['adobe'], 'color': this.colorScheme.domain[3], 'urlParams': adobeParams},
          {'name': 'Secure Drive', 'value': results['secureDrive'], 'color': this.colorScheme.domain[4], 'urlParams': secureDriveParams}
        ];

        this.totalDevices = results['totalDevices'];
        if ( this.testDisplay ) {
          this.testTotal += 1;
          this.totalDevices = this.testTotal;
        }

        // this code below automatically figures out the
        // best number of segments to display on the gauge
        if ( this.totalDevices > 0 ) {
          this.showAxis = true;
          if ( this.totalDevices <= 20 ) {
            this.bigSegments = this.totalDevices;
          } else {
            this.bigSegments = 0;
            while ( this.bigSegments === 0 ) {
              for ( let i = 12; i >= 4; i--) {
                if (this.totalDevices % i === 0 ) {
                  this.bigSegments = i;
                  break;
                }
              }
              // didn't find a good devisor add 1
              if ( this.bigSegments === 0 ) {
                this.totalDevices += 1;
              }
            }

          }
        } else {
          this.totalDevices = 0;
          this.showAxis = false;
        }

        if ( this.testDisplay ) {
          setTimeout(() => { this.getDeviceConfig(); } , 3000);
        }

        this.dashboardConfigLoaded = true;
        this.loadingConfig$.next(false);
    },
    () => this.loadingConfig$.next(false));
  }

  resetData(initialize = false) {
    this.showAxis = false;
    this.totalDevices = initialize ? 0 : 1;
    this.bigSegments = 1;
    this.data = [
      {'name': this.fFolder, 'value': 0, 'color': this.colorScheme.domain[0], 'urlParams': {}},
      {'name': this.fCustomFile, 'value': 0, 'color': this.colorScheme.domain[1], 'urlParams': {}},
      {'name': 'Microsoft Office', 'value': 0, 'color': this.colorScheme.domain[2], 'urlParams': {}},
      {'name': 'Adobe', 'value': 0, 'color': this.colorScheme.domain[3], 'urlParams': {}},
      {'name': 'Secure Drive', 'value': 0, 'color': this.colorScheme.domain[4], 'urlParams': {}}
    ];
  }

  getHeaderQueryParams() {
    const headerQueryParams = {};
    if ( this.deviceSet !== undefined && this.deviceSet != null ) {
      headerQueryParams['deviceSetId'] = this.deviceSet.id.toString();
    }
    return headerQueryParams;
  }

}
